import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    TextField,
    Button,
    Stack,
    Alert,
    InputAdornment,
    useTheme,
    alpha
} from '@mui/material';
import {
    TrendingUp as TrendingUpIcon,
    Save as SaveIcon,
    AutoFixHigh as PerfectBatchIcon
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';
import { supabase } from '../../../../supabaseClient';
import { usePermissions } from '../../../../utils/permissions';
import { useNavigate } from 'react-router-dom';

const SummaryLog = ({ lineLogId, lineProductionId, lineTimeFormat = '12hour', lineProductionData }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { hasPermission } = usePermissions();
    const [summaryData, setSummaryData] = useState({
        // Essential Fields Only
        total_units_produced: '',
        units_set_aside: '',
        production_end_time: null,
        operator_comments: ''
    });

    const [plannedQuantity, setPlannedQuantity] = useState(0);
    const [errors, setErrors] = useState({});
    const [saveSuccess, setSaveSuccess] = useState(false);

    // Load planned quantity and existing yield data
    useEffect(() => {
        if (lineProductionId) {
            loadPlannedQuantity();
        }
        if (lineLogId) {
            loadExistingYieldData();
        } else {
            // No log yet — auto-fill end time from planned completion
            const plannedEndTime = lineProductionData?.planned_completion_date && lineProductionData?.planned_completion_time
                ? dayjs(`${lineProductionData.planned_completion_date} ${lineProductionData.planned_completion_time}`, 'YYYY-MM-DD HH:mm')
                : null;
            if (plannedEndTime) {
                setSummaryData(prev => ({ ...prev, production_end_time: plannedEndTime }));
            }
        }
    }, [lineProductionId, lineLogId, lineProductionData?.planned_completion_date, lineProductionData?.planned_completion_time]);

    const loadPlannedQuantity = async () => {
        try {
            const { data, error } = await supabase
                .from('line_productions')
                .select('planned_quantity')
                .eq('id', lineProductionId)
                .single();

            if (error) {
                console.error('Error loading planned quantity:', error);
                return;
            }

            if (data) {
                setPlannedQuantity(data.planned_quantity || 0);
            }
        } catch (error) {
            console.error('Error loading planned quantity:', error);
        }
    };

    const loadExistingYieldData = async () => {
        try {
            const { data, error } = await supabase
                .from('production_line_log_yield')
                .select('*')
                .eq('line_log_id', lineLogId)
                .single();

            if (error && error.code !== 'PGRST116') {
                console.error('Error loading existing yield data:', error);
                return;
            }

            if (data) {
                setSummaryData({
                    total_units_produced: data.total_units_produced?.toString() || '',
                    units_set_aside: data.units_set_aside?.toString() || '',
                    production_end_time: data.production_end_time ? dayjs(data.production_end_time) : null,
                    operator_comments: data.operator_comments || ''
                });
            } else {
                // No existing yield data - auto-fill end time from planned completion
                const plannedEndTime = lineProductionData?.planned_completion_date && lineProductionData?.planned_completion_time
                    ? dayjs(`${lineProductionData.planned_completion_date} ${lineProductionData.planned_completion_time}`, 'YYYY-MM-DD HH:mm')
                    : null;
                if (plannedEndTime) {
                    setSummaryData(prev => ({ ...prev, production_end_time: plannedEndTime }));
                }
            }
        } catch (error) {
            console.error('Error loading existing yield data:', error);
        }
    };

    const validateForm = () => {
        const newErrors = {};
        
        if (!summaryData.total_units_produced.trim()) {
            newErrors.total_units_produced = 'Total units produced is required';
        }
        
        if (!summaryData.production_end_time) {
            newErrors.production_end_time = 'Production end time is required';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        // Check permissions before proceeding
        if (!canEdit) {
            setErrors({ submit: 'You do not have permission to save yield summary.' });
            return;
        }

        try {
            setSaveSuccess(false);
            setErrors({});
            
            if (!validateForm()) {
                return;
            }
            
            // Get company_id from the current user
            const { data: { user }, error: userError } = await supabase.auth.getUser();
            if (userError || !user) {
                setErrors({ submit: 'Authentication error. Please log in again.' });
                return;
            }

            // Get company_id
            let companyId;
            const { data: companyData, error: companyError } = await supabase
                .from('companies')
                .select('id')
                .eq('created_by', user.id)
                .single();

            if (companyError && companyError.code === 'PGRST116') {
                // User is not company owner, check if they're a company user
                const { data: companyUserData, error: memberError } = await supabase
                    .from('company_users')
                    .select('company_id')
                    .eq('user_id', user.id)
                    .eq('status', 'active')
                    .single();

                if (memberError || !companyUserData) {
                    setErrors({ submit: 'No company found for your account.' });
                    return;
                }
                companyId = companyUserData.company_id;
            } else if (companyError) {
                setErrors({ submit: `Error getting company: ${companyError.message}` });
                return;
            } else {
                companyId = companyData.id;
            }

            // First check if yield record already exists
            const { data: existingYield, error: checkError } = await supabase
                .from('production_line_log_yield')
                .select('id')
                .eq('line_log_id', lineLogId)
                .single();

            const yieldData = {
                line_log_id: lineLogId,
                line_production_id: lineProductionId,
                company_id: companyId,
                total_units_produced: parseFloat(summaryData.total_units_produced) || 0,
                units_set_aside: parseFloat(summaryData.units_set_aside) || 0,
                production_end_time: summaryData.production_end_time?.toISOString(),
                operator_comments: summaryData.operator_comments || null,
                yield_summary_completed: true,
                yield_summary_completed_at: dayjs().toISOString(),
                yield_summary_completed_by: user.email || 'Unknown User',
                updated_by: user.id,
                updated_at: dayjs().toISOString()
            };

            let saveError;
            if (existingYield) {
                // Update existing record
                const { error } = await supabase
                    .from('production_line_log_yield')
                    .update(yieldData)
                    .eq('id', existingYield.id);
                saveError = error;
            } else {
                // Insert new record
                yieldData.created_by = user.id;
                yieldData.created_at = dayjs().toISOString();
                
                const { error } = await supabase
                    .from('production_line_log_yield')
                    .insert([yieldData]);
                saveError = error;
            }

            if (saveError) {
                console.error('Error saving yield summary:', saveError);
                setErrors({ submit: `Database error: ${saveError.message}` });
                return;
            }

            // Also update the production_line_logs completion status
            await supabase
                .from('production_line_logs')
                .update({
                    summary_log_completed: true,
                    summary_log_completed_at: dayjs().toISOString(),
                    summary_log_completed_by: user.email || 'Unknown User'
                })
                .eq('id', lineLogId);
            
            setSaveSuccess(true);
            setTimeout(() => setSaveSuccess(false), 3000);
        } catch (error) {
            console.error('Error saving yield summary:', error);
            setErrors({ submit: 'Unexpected error occurred while saving' });
        }
    };

    const formatNumber = (num) => {
        return (num || 0).toLocaleString();
    };

    // Permission check - must be after all hooks
    if (!hasPermission('production.line.log.yield.view', 'read')) {
        return (
            <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                minHeight: '60vh',
                textAlign: 'center',
                width: '100%',
                px: 3,
                py: 8
            }}>
                <Card sx={{
                    p: 6,
                    borderRadius: 3,
                    border: `1px solid ${theme.palette.grey[400]}`,
                    background: 'white',
                    boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
                    maxWidth: 500,
                    width: '100%',
                    position: 'relative',
                    overflow: 'hidden',
                    cursor: 'default',
                    transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                    '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                    },
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 4,
                        background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
                        opacity: 0.8,
                        transition: 'opacity 0.3s ease'
                    },
                    '&:hover::before': {
                        opacity: 1
                    }
                }}>
                    <Box sx={{ 
                        p: 2, 
                        borderRadius: 3, 
                        background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                        mb: 4,
                        display: 'inline-flex',
                        boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
                    }}>
                        <TrendingUpIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
                    </Box>
                    <Typography variant="h4" sx={{ 
                        mb: 2, 
                        fontWeight: 500, 
                        color: 'text.primary',
                        fontSize: '2rem',
                        letterSpacing: '-0.5px'
                    }}>
                        Access Restricted
                    </Typography>
                    <Typography variant="h6" sx={{ 
                        color: 'text.secondary', 
                        mb: 3,
                        fontWeight: 500,
                        fontSize: '1.1rem'
                    }}>
                        Yield Log Permissions Required
                    </Typography>
                    <Typography variant="body1" sx={{ 
                        color: 'text.secondary',
                        lineHeight: 1.6,
                        mb: 4,
                        fontSize: '1rem',
                        maxWidth: 400,
                        mx: 'auto'
                    }}>
                        You don't have permission to view the Yield Log. 
                        Contact your administrator to request access.
                    </Typography>
                </Card>
            </Box>
        );
    }

    // Check edit permissions  
    const canEdit = hasPermission('production.line.log.yield.edit', 'write');

    return (
        <Box>
            {/* Error Messages */}
            {Object.keys(errors).length > 0 && (
                <Alert 
                    severity="error" 
                    sx={{ 
                        mb: 4,
                        borderRadius: 2,
                        border: `1px solid ${theme.palette.error.main}`,
                        backgroundColor: alpha(theme.palette.error.main, 0.1),
                        fontWeight: 500
                    }}
                >
                    <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 1, color: 'black' }}>
                        Please fix the following errors:
                    </Typography>
                    {Object.entries(errors).map(([field, message]) => (
                        <Typography key={field} variant="body2" sx={{ display: 'block', color: 'black' }}>
                            • {message}
                        </Typography>
                    ))}
                </Alert>
            )}

            {/* Success Message */}
            {saveSuccess && (
                <Alert 
                    severity="success" 
                    sx={{ 
                        mb: 4,
                        borderRadius: 2,
                        border: `1px solid ${theme.palette.success.main}`,
                        backgroundColor: alpha(theme.palette.success.main, 0.1),
                        fontWeight: 500
                    }}
                >
                    <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                        Yield summary saved successfully!
                    </Typography>
                </Alert>
            )}

            {/* Header Section */}
            <Card sx={{ 
                borderRadius: 3,
                mb: 4,
                border: `1px solid ${theme.palette.grey[400]}`,
                background: 'white',
                boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}>
                <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box sx={{ flex: 1 }}>
                            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                                <Box sx={{ 
                                    p: 1.5, 
                                    borderRadius: 2, 
                                    background: alpha(theme.palette.primary.main, 0.1),
                                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                                }}>
                                    <TrendingUpIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                                </Box>
                                <Box>
                                    <Typography variant="h5" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 0.5 }}>
                                        Yield Summary
                                    </Typography>
                                    <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                                        Record production yield and waste data
                                    </Typography>
                                </Box>
                            </Stack>
                        </Box>
                        
                        <Button 
                            variant="contained"
                            onClick={handleSave}
                            startIcon={<SaveIcon />}
                            size="large"
                            disabled={!canEdit}
                            sx={{
                                borderRadius: 2,
                                px: 4,
                                py: 1.5,
                                fontWeight: 500,
                                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                                '&:hover': {
                                    background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                                    transform: 'translateY(-1px)',
                                    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.35)}`
                                },
                                transition: 'all 0.2s ease'
                            }}
                        >
                            Save Yield Summary
                        </Button>
                    </Box>
                </CardContent>
            </Card>

            {/* Simple Form */}
            <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                    <Card sx={{ 
                        borderRadius: 3, 
                        border: `1px solid ${theme.palette.grey[400]}`,
                        background: 'white',
                        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                        '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                        },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}>
                        <CardContent sx={{ p: 4 }}>
                            <Typography variant="h6" sx={{ fontWeight: 500, mb: 3, color: theme.palette.text.primary }}>
                                Yield Results
                            </Typography>
                            
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Total Units Produced *"
                                        type="number"
                                        value={summaryData.total_units_produced}
                                        onChange={(e) => canEdit ? setSummaryData({...summaryData, total_units_produced: e.target.value}) : null}
                                        disabled={!canEdit}
                                        error={!!errors.total_units_produced}
                                        helperText={errors.total_units_produced}
                                        required
                                        InputProps={{
                                            endAdornment: canEdit ? (
                                                <InputAdornment position="end">
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        startIcon={<PerfectBatchIcon fontSize="small" />}
                                                        onClick={() => setSummaryData(prev => ({
                                                            ...prev,
                                                            total_units_produced: (lineProductionData?.units_to_make || plannedQuantity || 0).toString(),
                                                            units_set_aside: '0'
                                                        }))}
                                                        sx={{ whiteSpace: 'nowrap', textTransform: 'none', fontSize: '0.75rem', borderRadius: 1.5 }}
                                                    >
                                                        Perfect Batch
                                                    </Button>
                                                </InputAdornment>
                                            ) : null
                                        }}
                                    />
                                </Grid>
                                
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Planned Quantity"
                                        type="number"
                                        value={plannedQuantity}
                                        disabled
                                        helperText="Auto-loaded from production line overview"
                                        sx={{
                                            '& .MuiInputBase-input.Mui-disabled': {
                                                WebkitTextFillColor: theme.palette.text.primary,
                                                opacity: 0.8
                                            }
                                        }}
                                    />
                                </Grid>
                                
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Units Set Aside (Waste)"
                                        type="number"
                                        value={summaryData.units_set_aside}
                                        onChange={(e) => canEdit ? setSummaryData({...summaryData, units_set_aside: e.target.value}) : null}
                                        disabled={!canEdit}
                                        helperText={canEdit ? "Products that were set aside or wasted" : "Waste units (read-only)"}
                                    />
                                </Grid>
                                
                                <Grid item xs={12} md={6}>
                                    <DateTimePicker
                                        label="Production End Time *"
                                        value={summaryData.production_end_time}
                                        onChange={(newValue) => canEdit ? setSummaryData({...summaryData, production_end_time: newValue}) : null}
                                        disabled={!canEdit}
                                        format={lineTimeFormat === '24hour' ? 'MM/DD/YYYY HH:mm' : 'MM/DD/YYYY h:mm A'}
                                        ampm={lineTimeFormat !== '24hour'}
                                        slotProps={{
                                            textField: {
                                                fullWidth: true,
                                                error: !!errors.production_end_time,
                                                helperText: errors.production_end_time,
                                                required: true,
                                                disabled: !canEdit
                                            }
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Card sx={{ 
                        borderRadius: 3, 
                        border: `1px solid ${theme.palette.grey[400]}`,
                        background: 'white',
                        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                        height: '100%',
                        '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                        },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}>
                        <CardContent sx={{ p: 4, height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="h6" sx={{ fontWeight: 500, mb: 3, color: theme.palette.text.primary }}>
                                Operator Notes
                            </Typography>
                            
                            <TextField
                                fullWidth
                                multiline
                                rows={8}
                                label="Comments"
                                value={summaryData.operator_comments}
                                onChange={(e) => canEdit ? setSummaryData({...summaryData, operator_comments: e.target.value}) : null}
                                disabled={!canEdit}
                                placeholder={canEdit ? "Any observations, issues, or notes about the production run..." : "Operator comments (read-only)"}
                                sx={{ flex: 1 }}
                            />
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default SummaryLog;